import { useCallback, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Library, Loader2, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  FileLibraryPickerDialog,
  FileSourceChooser,
  fileMatchesImportSourceAccept,
} from '@/features/file-library';
import { IMPORT_SOURCE_MIME_GROUPS } from '@/features/file-library/mimeGroups';
import type { FileLibrarySelection, FileSourceMode } from '@/features/file-library/types';
import { cn } from '@/lib/utils';

interface SourceFileDropzoneProps {
  isUploading?: boolean;
  onFileSelected: (file: File) => void;
  selectedFile?: File | null;
  disabled?: boolean;
  libraryAvailable?: boolean;
  sourceMode?: FileSourceMode;
  onSourceModeChange?: (mode: FileSourceMode) => void;
  selectedLibraryFile?: FileLibrarySelection | null;
  onLibraryFileSelected?: (file: FileLibrarySelection) => void;
  batchId?: string;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const SourceFileDropzone = ({
  isUploading,
  onFileSelected,
  selectedFile,
  disabled,
  libraryAvailable = false,
  sourceMode = 'local',
  onSourceModeChange,
  selectedLibraryFile,
  onLibraryFileSelected,
  batchId,
}: SourceFileDropzoneProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const effectiveMode = libraryAvailable ? sourceMode : 'local';
  const interactionDisabled = disabled || isUploading;

  const triggerSelect = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) onFileSelected(file);
      event.target.value = '';
    },
    [onFileSelected],
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(false);
      if (interactionDisabled || effectiveMode !== 'local') return;
      const file = event.dataTransfer.files?.[0];
      if (file) onFileSelected(file);
    },
    [effectiveMode, interactionDisabled, onFileSelected],
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragOver(false), []);

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed border-efs-border bg-efs-bg-subtle px-6 py-10 text-center transition-colors',
        isDragOver && 'border-efs-info bg-efs-bg-panel',
        disabled && 'opacity-60',
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      role="region"
      aria-label={t('efs.imports.dropzone.label', { ns: 'imports' })}
    >
      {libraryAvailable ? (
        <FileSourceChooser
          value={effectiveMode}
          onChange={(mode) => onSourceModeChange?.(mode)}
          disabled={interactionDisabled}
        />
      ) : null}
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept=".xlsx,.docx,.pdf"
        onChange={handleChange}
        disabled={interactionDisabled || effectiveMode !== 'local'}
      />
      {effectiveMode === 'local' ? (
        <>
          <Upload className="h-8 w-8 text-efs-text-muted" />
          <div className="text-sm font-medium text-efs-text-primary">
            {t('efs.imports.dropzone.label', { ns: 'imports' })}
          </div>
          <div className="text-xs text-efs-text-muted">
            {t('efs.imports.dropzone.hint', { ns: 'imports' })}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={triggerSelect}
            disabled={interactionDisabled}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('efs.imports.dropzone.uploading', { ns: 'imports' })}
              </>
            ) : (
              t('efs.imports.dropzone.select_file', { ns: 'imports' })
            )}
          </Button>
        </>
      ) : (
        <>
          <Library className="h-8 w-8 text-efs-text-muted" />
          <div className="text-sm font-medium text-efs-text-primary">
            {t('efs.file_library.picker.title', { ns: 'fileLibrary' })}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPickerOpen(true)}
            disabled={interactionDisabled}
          >
            {t('efs.file_library.picker.choose', { ns: 'fileLibrary' })}
          </Button>
        </>
      )}

      {effectiveMode === 'local' && selectedFile ? (
        <dl className="mt-4 grid w-full max-w-md grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-left text-xs">
          <dt className="text-efs-text-muted">
            {t('efs.imports.dropzone.file_name', { ns: 'imports' })}
          </dt>
          <dd className="break-all font-mono">{selectedFile.name}</dd>
          <dt className="text-efs-text-muted">
            {t('efs.imports.dropzone.file_size', { ns: 'imports' })}
          </dt>
          <dd className="font-mono">{formatSize(selectedFile.size)}</dd>
        </dl>
      ) : null}
      {effectiveMode === 'library' && selectedLibraryFile ? (
        <dl className="mt-4 grid w-full max-w-md grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-left text-xs">
          <dt className="text-efs-text-muted">
            {t('efs.imports.dropzone.file_name', { ns: 'imports' })}
          </dt>
          <dd className="flex items-center gap-1 break-all font-mono">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-efs-success" />
            {selectedLibraryFile.display_name}
          </dd>
          <dt className="text-efs-text-muted">
            {t('efs.imports.dropzone.file_size', { ns: 'imports' })}
          </dt>
          <dd className="font-mono">{formatSize(selectedLibraryFile.size_bytes)}</dd>
        </dl>
      ) : null}
      {libraryAvailable && onLibraryFileSelected ? (
        <FileLibraryPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={onLibraryFileSelected}
          batchId={batchId}
          acceptMimeGroups={IMPORT_SOURCE_MIME_GROUPS}
          acceptFile={fileMatchesImportSourceAccept}
        />
      ) : null}
    </div>
  );
};
